import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { authApi } from '@/services/api';
import { ShieldCheck, ShieldAlert, Eye, EyeOff } from 'lucide-react';

type View = 'status' | 'setup' | 'recovery' | 'disable';

const AccountSecurityPage = () => {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState<boolean>(user?.totpEnabled === true);
  const [view, setView] = useState<View>('status');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // setup state
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  // disable state
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const startSetup = async () => {
    setError('');
    setIsLoading(true);
    try {
      const res = await authApi.setup2FA();
      if (res.success && res.data) {
        setQrDataUrl(res.data.qrDataUrl);
        setSecret(res.data.secret);
        setCode('');
        setView('setup');
      } else {
        setError(res.error || 'Failed to start 2FA setup');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to start 2FA setup');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const res = await authApi.verify2FA(code.trim());
      if (res.success && res.data) {
        setRecoveryCodes(res.data.recoveryCodes);
        setEnabled(true);
        setView('recovery');
      } else {
        setError(res.error || 'Verification failed');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const res = await authApi.disable2FA(password);
      if (res.success) {
        setEnabled(false);
        setPassword('');
        setView('status');
      } else {
        setError(res.error || 'Failed to disable 2FA');
      }
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to disable 2FA');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold font-heading text-[#1A1F2B] mb-1">Account Security</h1>
        <p className="text-[#6B7280] text-[15px]">Manage two-factor authentication for your account</p>
      </div>

      <div className="md-card p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          {enabled ? (
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
          ) : (
            <ShieldAlert className="w-6 h-6 text-amber-600" />
          )}
          <div>
            <h2 className="text-lg font-bold font-heading text-[#1A1F2B]">Two-Factor Authentication</h2>
            <p className="text-sm text-[#6B7280]">
              {enabled
                ? 'Enabled — a code from your authenticator app is required at login.'
                : 'Not enabled — add an extra layer of security to your account.'}
            </p>
          </div>
        </div>

        {error && (
          <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl mb-4">{error}</div>
        )}

        {/* STATUS */}
        {view === 'status' && (
          <div>
            {enabled ? (
              <button onClick={() => { setError(''); setPassword(''); setView('disable'); }} className="md-btn md-btn-danger text-[13px]">
                Disable 2FA
              </button>
            ) : (
              <button onClick={startSetup} disabled={isLoading} className="md-btn md-btn-primary text-[13px]">
                {isLoading ? 'Starting…' : 'Enable 2FA'}
              </button>
            )}
          </div>
        )}

        {/* SETUP */}
        {view === 'setup' && (
          <form onSubmit={confirmSetup} className="space-y-4">
            <ol className="text-sm text-[#374151] space-y-1 list-decimal list-inside">
              <li>Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password…).</li>
              <li>Enter the 6-digit code it shows to confirm.</li>
            </ol>
            {qrDataUrl && (
              <img src={qrDataUrl} alt="2FA QR code" className="w-44 h-44 border border-[#ECEEF1] rounded-xl" />
            )}
            <div>
              <p className="text-xs text-[#9CA3AF] mb-1">Can't scan? Enter this key manually:</p>
              <code className="text-xs font-mono bg-[#F9FAFB] border border-[#ECEEF1] rounded px-2 py-1 break-all">{secret}</code>
            </div>
            <div>
              <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Verification code</label>
              <div className="md-outlined-textfield">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  autoComplete="one-time-code"
                  className="w-full"
                  required
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setView('status'); setError(''); }} className="md-btn md-btn-outlined text-[13px]">
                Cancel
              </button>
              <button type="submit" disabled={isLoading} className="md-btn md-btn-primary text-[13px]">
                {isLoading ? 'Verifying…' : 'Verify & Enable'}
              </button>
            </div>
          </form>
        )}

        {/* RECOVERY CODES */}
        {view === 'recovery' && (
          <div className="space-y-4">
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
              <strong>Save these recovery codes now.</strong> Each can be used once if you lose access to your
              authenticator app. They will not be shown again.
            </div>
            <div className="grid grid-cols-2 gap-2">
              {recoveryCodes.map((rc) => (
                <code key={rc} className="text-sm font-mono bg-[#F9FAFB] border border-[#ECEEF1] rounded px-2 py-1.5 text-center">
                  {rc}
                </code>
              ))}
            </div>
            <button onClick={() => setView('status')} className="md-btn md-btn-primary text-[13px]">
              I've saved my recovery codes
            </button>
          </div>
        )}

        {/* DISABLE */}
        {view === 'disable' && (
          <form onSubmit={confirmDisable} className="space-y-4">
            <p className="text-sm text-[#374151]">Enter your password to turn off two-factor authentication.</p>
            <div>
              <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Password</label>
              <div className="md-outlined-textfield relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your current password"
                  className="w-full pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setView('status'); setError(''); }} className="md-btn md-btn-outlined text-[13px]">
                Cancel
              </button>
              <button type="submit" disabled={isLoading} className="md-btn md-btn-danger text-[13px]">
                {isLoading ? 'Disabling…' : 'Disable 2FA'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AccountSecurityPage;
